import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import Box from 'ui-box';
import config from '../../config';

import {
  Container,
  Grid,
  Heading,
  majorScale,
  PageContainer,
  Card,
  Tag,
  Icon,
  defaultFills,
} from '../../npaper';
import { IOrganisation, IProduct, ISale, useStores } from '../../stores';
import { IBankDetails } from '../../stores/BankDetailsStore';
import { IIntegration } from '../../stores/IntegrationStore';

export const QuickStartGuide = () => {
  const intl = useIntl();
  const history = useHistory();
  const {
    authStore,
    bankDetailsStore,
    productStore,
    saleStore,
    integrationStore,
  } = useStores();
  const [organisation, setOrganisation] = useState<IOrganisation>();
  const [bankDetails, setBankDetails] = useState<IBankDetails[]>();
  const [products, setProducts] = useState<IProduct[]>();
  const [sales, setSales] = useState<ISale[]>();
  const [integrations, setIntegrations] = useState<IIntegration[]>();

  useEffect(() => {
    setOrganisation(authStore.currentOrganisation as IOrganisation);
  }, [authStore.currentOrganisation]);

  useEffect(() => {
    let isSub = true;
    Promise.all([
      bankDetailsStore.fetchMore({}, 1),
      productStore.fetchMore({}, 1),
      saleStore.fetchMore({}, 1),
      integrationStore.fetchMore({}, 1),
    ]).then(([bankDetails, products, sales, integrations]) => {
      if (isSub) {
        setBankDetails(bankDetails.items);
        setProducts(products.items);
        setSales(sales.items);
        setIntegrations(integrations.items);
      }
    });
    return () => {
      isSub = false;
    };
  }, [bankDetailsStore, integrationStore, productStore, saleStore]);

  const hasBankDetails = bankDetails && bankDetails.length > 0;
  const hasLogo = organisation && organisation.logo;
  const hasIntegration = integrations && integrations.length > 0;
  const hasProducts = products && products.length > 0;
  const hasSales = sales && sales.length > 0;

  return (
    <PageContainer paddingBottom={majorScale(4)}>
      <Container>
        <Grid>
          <div className="col-span-12 lg:col-span-6">
            <Box marginBottom={majorScale(4)}>
              <Heading size={700} marginBottom={majorScale(1)}>
                <FormattedMessage defaultMessage="Welcome to Sails!" />
              </Heading>
              <p className="text-base">
                <FormattedMessage defaultMessage="We are ecstatic about your decision to get started with a new user-friendly sales system. Our Quick Start guide helps you get up and running quickly and prepares you for your first sale with Sails." />
              </p>
            </Box>
            <Heading size={400}>
              <FormattedMessage defaultMessage="Quick Start guide" />
            </Heading>
            {[
              {
                title: intl.formatMessage({
                  defaultMessage: 'Create your account',
                }),
                message: intl.formatMessage({
                  defaultMessage: 'Provide some details about your business',
                }),
                done: true,
              },
              {
                title: intl.formatMessage({
                  defaultMessage: 'Make your brand visible',
                }),
                message: intl.formatMessage({
                  defaultMessage:
                    'Upload your logo and an icon to make your brand visible',
                }),
                done: hasLogo,
                linkTo: '/settings/account',
              },
              {
                title: intl.formatMessage({
                  defaultMessage: 'Add a currency and bank account',
                }),
                message: intl.formatMessage({
                  defaultMessage:
                    'Provide some details of your bank account to ensure your customers know how to pay your invoices',
                }),
                done: hasBankDetails,
                linkTo: '/settings/bank-details',
              },
              {
                title: intl.formatMessage({
                  defaultMessage: 'Connect to your accounting software',
                }),
                message: intl.formatMessage({
                  defaultMessage:
                    'Integrate with your accounting software to automatically setup your business and ensure automatic bookkeeping',
                }),
                done: hasIntegration,
                linkTo: '/settings/integrations',
              },
              {
                title: intl.formatMessage({
                  defaultMessage: 'Add your customers, contacts, and products',
                }),
                message: intl.formatMessage({
                  defaultMessage:
                    'Collect all your important business data in one place',
                }),
                done: hasProducts,
                linkTo: '/products',
              },
              {
                title: intl.formatMessage({
                  defaultMessage: 'Create your first sale',
                }),
                message: intl.formatMessage({
                  defaultMessage:
                    'Create your first offer and send it to a customer',
                }),
                done: hasSales,
                linkTo: '/offers',
              },
            ].map((item, i) => (
              <Card
                key={i}
                padding={majorScale(2)}
                display="flex"
                alignItems="center"
                marginTop={majorScale(2)}
                interactive={!item.done}
                backgroundColor={
                  item.done ? defaultFills.normal[50] : undefined
                }
                onClick={() =>
                  !item.done && item.linkTo && history.push(item.linkTo)
                }
              >
                <Tag
                  marginRight={majorScale(2)}
                  intent={item.done ? 'success' : 'none'}
                >
                  {i + 1}
                </Tag>
                <div>
                  <Heading
                    className={item.done ? 'muted' : undefined}
                    size={300}
                  >
                    {item.title}
                  </Heading>
                  <p className={item.done ? 'muted' : undefined}>
                    {item.message}
                  </p>
                </div>

                <Box marginLeft="auto">
                  {item.done && (
                    <Icon icon="tick" color={defaultFills.success[500]} />
                  )}
                  {!item.done && (
                    <Icon icon="chevron-right" color={defaultFills.muted} />
                  )}
                </Box>
              </Card>
            ))}

            <Box marginTop={majorScale(4)} maxWidth="440px">
              <Heading size={400} marginBottom={majorScale(1)}>
                <FormattedMessage defaultMessage="Do you need assistance to get started?" />
              </Heading>
              <p>
                <FormattedMessage defaultMessage="We are ready to help you if you run into trouble or have any questions. Grab the phone - we speak both English and Danish." />
              </p>
              <Box display="flex" alignItems="center" marginTop={majorScale(2)}>
                <Icon icon="phone" marginRight={majorScale(1)} />
                <Box is="p" className="font-mono medium">
                  {config.support.phone}
                </Box>
              </Box>
              <Box display="flex" alignItems="center" marginTop={majorScale(2)}>
                <Icon icon="envelope" marginRight={majorScale(1)} />
                <Box is="p" className="font-mono medium">
                  {config.support.email}
                </Box>
              </Box>
            </Box>
          </div>
          <Box
            className="col-span-12 lg:col-span-6 mt-8 md:mt-40"
            textAlign="center"
          />
        </Grid>
      </Container>
    </PageContainer>
  );
};
